import {
  FEATURES,
  FEATURE_KEYS,
  isFeatureKey,
  type FeatureDefinition,
  type FeatureKey,
} from "./features";
import { accessibleFacilityIds, withFrontDeskReach } from "./scope";
import { isUnrestricted, type PolicySubject } from "./subject";

/* Always on, whatever an override says: the dashboard is the way back to
   everything else, and Settings holds a person's own profile. */
const LOCKED: ReadonlySet<string> = new Set<FeatureKey>(["dashboard", "settings"]);

export function isLockedFeature(key: string): boolean {
  return LOCKED.has(key);
}

interface FeatureOptions {
  facilityId?: string | null;
}

/** Order: Dashboard and Settings always on; opt-in key only by override; platform admin outside an
 * organization gets the rest; facility then organization override; a following key follows its
 * target; else the plan; no resolvable plan is `unrestricted`. */
export function isFeatureEnabled(
  subject: PolicySubject,
  key: FeatureKey,
  options: FeatureOptions = {},
): boolean {
  if (LOCKED.has(key)) return true;
  const definition: FeatureDefinition = FEATURES[key];
  if (definition.optIn) return findOverride(subject, key, options.facilityId) === true;
  if (isUnrestricted(subject)) return true;
  const override = findOverride(subject, key, options.facilityId);
  if (override !== undefined) return override;
  if (
    definition.follows !== undefined &&
    definition.follows !== key &&
    isFeatureKey(definition.follows)
  ) {
    return isFeatureEnabled(subject, definition.follows, options);
  }
  if (definition.alwaysOn) return true;
  if (subject.entitlements.unrestricted) return true;
  return subject.entitlements.featureKeys.includes(key);
}

function findOverride(
  subject: PolicySubject,
  key: FeatureKey,
  facilityId: string | null | undefined,
): boolean | undefined {
  if (facilityId) {
    const facility = subject.overrides.find(
      (o) => o.scope === "facility" && o.scopeId === facilityId && o.featureKey === key,
    );
    if (facility) return facility.enabled;
  }
  const organizationId = subject.organization?.id;
  if (!organizationId) return undefined;
  const organization = subject.overrides.find(
    (o) => o.scope === "organization" && o.scopeId === organizationId && o.featureKey === key,
  );
  return organization?.enabled;
}

/** A feature at one building, or with none named: on if the organization or any building in the
 * caller's scope has it (front desk roster included). An owner reaches every building with no
 * membership row. The API decides per request at the named building. */
export function isFeatureEnabledAt(
  subject: PolicySubject,
  key: FeatureKey,
  facilityId: string | null,
): boolean {
  if (facilityId) return isFeatureEnabled(subject, key, { facilityId });
  if (isFeatureEnabled(subject, key)) return true;
  const reach = accessibleFacilityIds(withFrontDeskReach(subject)) ?? [];
  return reach.some((id) => isFeatureEnabled(subject, key, { facilityId: id }));
}

/** The caller as a procedure behind `key` sees them when no building is named: only buildings where
 * the module is on, so a list across all of theirs never reaches one where it is off. Unchanged
 * when on everywhere. */
export function scopedToFeature(subject: PolicySubject, key: FeatureKey): PolicySubject {
  const organization = subject.organization;
  if (isUnrestricted(subject) || !organization) return subject;
  const on = organization.facilityIds.filter((facilityId) =>
    isFeatureEnabled(subject, key, { facilityId }),
  );
  if (on.length === organization.facilityIds.length) return subject;
  const kept = new Set(on);
  return {
    ...subject,
    organization: { ...organization, facilityIds: on },
    facilities: Object.fromEntries(
      Object.entries(subject.facilities).filter(([facilityId]) => kept.has(facilityId)),
    ),
  };
}

/** Every feature the subject can currently use; drives menus and the settings console. */
export function enabledFeatures(subject: PolicySubject, options?: FeatureOptions): FeatureKey[] {
  return FEATURE_KEYS.filter((key) => isFeatureEnabled(subject, key, options));
}
